/**
 * Chart colour for the dashboard.
 *
 * The screen's surfaces, ink and radii come from the app's own tokens
 * (`containers/customer/customers.theme.js`); only the marks need colours of
 * their own, and those were chosen against the app's white cards and checked
 * rather than eyeballed.
 *
 * SERIES — the three that tell categories apart. Worst pair, across all three
 * pairings and simulated for every kind of colour blindness, separates by
 * ΔE 11.5 (the bar is 8), and all three clear 3:1 against white. Three is the
 * ceiling: a fourth hue cannot be added without one pair falling below it, so
 * anything with more classes than this is a list, not a chart.
 *
 * The brand orange keeps slot 2 so the till still looks like itself.
 */
export const SERIES = ['#1D4ED8', '#F64829', '#0F9D70'];

/**
 * EMPHASIS — one thing at a time.
 *
 * Most charts here plot a single measure, and the honest form is one accent
 * against a quiet grey rather than a rainbow: `context` for the days that are
 * only there for scale, `accent` for the day being talked about, `peak` for
 * the best one. All three are the brand hue or a neutral, so nothing has to be
 * learned.
 */
export const EMPHASIS = {
  context: '#CBD1D8',
  accent: '#F64829',
  peak: '#8A230F',
  track: '#F0F1F4',
};

/** Chart chrome, one step off the surface so it never competes with the data. */
export const CHROME = {
  grid: '#F0F1F4',
  axis: '#E6E8EC',
  label: '#9AA1AC',
};

/**
 * Money, short.
 *
 * Axis ticks and tight columns cannot carry `$1,284.50`, and a chart does not
 * need the cents — the figure beside it does.
 */
export function shortMoney(amount, mark = '$') {
  const value = Number(amount || 0);
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  if (abs >= 1000) {
    const thousands = abs / 1000;
    return `${sign}${mark}${thousands >= 10 ? Math.round(thousands) : thousands.toFixed(1)}K`;
  }
  return `${sign}${mark}${Math.round(abs)}`;
}

/** `1,284` — grouped, no currency, for counts and plate numbers. */
export function grouped(value) {
  return Number(value || 0).toLocaleString('en-US');
}

/**
 * A round number at or above the tallest bar, so the top gridline is a figure
 * anyone can read: 800, 1,000, 1,600, 2,000 — never 1,517.
 */
export function niceCeiling(max) {
  const value = Number(max || 0);
  if (value <= 0) return 100;
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
  const steps = [1, 1.25, 1.5, 2, 2.5, 3, 4, 5, 7.5, 10];
  for (let i = 0; i < steps.length; i += 1) {
    const candidate = steps[i] * magnitude;
    if (candidate >= value) return candidate;
  }
  return 10 * magnitude;
}
